import { ApiResponse } from 'apisauce';
import * as R from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { paginate } from '../../lib/nest';
import { restApi } from '../../services/api';
import { PaginatedResponse } from '../../types/request.types';
import useDebounceFunction from '../Hooks/useDebouncedFunction';
import {
  ListPickProps,
  ListPickWithCustomFetcherProps,
  UseListPickActions,
} from './useListPick.types';

const DEFAULT_LIMIT = 20;

const fetch = async (
  allParams: any,
  url?: string,
  axiosConfig?: any,
  customFetcher?: ListPickWithCustomFetcherProps['customFetcher'],
) => {
  const { debounceName, ...params } = allParams;

  if (customFetcher) {
    return customFetcher(params);
  }

  if (url) {
    return restApi.get<any>(url, params, axiosConfig);
  }
};

type Selected<T> = T & { id: string };

const axiosConfigDefaultValue = {};
export const useListPick = <T>({
  url,
  defaultQuery,
  queryField,
  limit,
  disabled,
  order,
  shouldFetch = true,
  axiosConfig = axiosConfigDefaultValue,
  customFetcher,
}: ListPickProps): UseListPickActions<T> => {
  const [internalShouldFetch, setInternalShouldFetch] = useState(shouldFetch);
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [nextQuery, setNextQuery] = useState('');
  const [selected, setSelected] = useState<Selected<T>[]>([]);
  const [loading, setLoading] = useState(false);

  const { handleDebounceFunction: debounceSearch } = useDebounceFunction({ debounceTime: 500 });

  const handleClear = useCallback(() => {
    setItems([]);
    setSelected([]);
    setNextQuery('');
    setCount(0);
    setPage(0);
  }, []);

  const fetchItems = useCallback(
    (query: string) => {
      const params = defaultQuery ?? {};
      setLoading(true);

      if (!R.isNil(query)) {
        params[queryField ?? 'query'] = query;
        if (query !== nextQuery) {
          setPage(0);
          setNextQuery(query);
        }
      }

      const allParams = {
        ...params,
        ...paginate.order(order),
        ...paginate.limit(limit || DEFAULT_LIMIT),
        ...paginate.skip((limit || DEFAULT_LIMIT) * page),
      };

      if (customFetcher) {
        return fetch(allParams, undefined, undefined, customFetcher);
      }

      return fetch(allParams, url, axiosConfig);
    },
    [axiosConfig, defaultQuery, limit, nextQuery, order, page, queryField, url],
  );

  const updateFetchItemsResult = useCallback(
    (response: ApiResponse<PaginatedResponse<T>>) => {
      if (response.ok && response.data) {
        const newItems = Array.isArray(response.data) ? response.data : response.data.data;
        if (page > 0) {
          setItems((prev) => [...prev, ...newItems]);
        } else {
          setItems(newItems);
        }
        setCount(response.data.count);
      } else {
        setItems([]);
      }
      setLoading(false);
    },
    [page],
  );

  const handleSearch = useCallback(
    async (query: string) => {
      setInternalShouldFetch(true);
      debounceSearch(async () => {
        setPage(0);
        setNextQuery(query);
      });
    },
    [fetchItems, updateFetchItemsResult],
  );

  useEffect(() => {
    let isMounted = true;

    if (disabled) {
      return;
    }

    const handleSearchOnRender = async (query: string) => {
      if (isMounted) {
        const response = await fetchItems(query);
        response && updateFetchItemsResult(response);
      }
    };

    if (internalShouldFetch || shouldFetch || page > 0) {
      handleSearchOnRender(nextQuery);
    }

    return () => {
      isMounted = false;
    };
  }, [page, nextQuery, internalShouldFetch, shouldFetch, url]);

  const handleNext = useCallback(() => {
    setPage((current) => current + 1);
  }, []);

  const hasNextPage = count - (page + 1) * (limit || DEFAULT_LIMIT) > 0;

  const handleAddItem = useCallback(
    async (item: any) => {
      if (selected.find((s) => s.id === item.id)) {
        return;
      }
      setSelected([...selected, item]);
    },
    [selected],
  );

  const handleRemoveItem = useCallback(
    async (item: any) => {
      setSelected(selected.filter((s) => s.id !== item.id));
    },
    [selected],
  );

  return {
    leftItems: items,
    rightItems: selected,
    hasNextPage,
    loading,
    onSearch: handleSearch,
    next: handleNext,
    onAddItem: handleAddItem,
    onRemoveItem: handleRemoveItem,
    clear: handleClear,
  };
};
